'use client';
import { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { twMerge } from 'tailwind-merge';
import Dropdown from '../ui/Dropdown';
import SafeImage from '@/components/ui/SafeImage';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Menu, ChevronRight } from 'lucide-react';
import NotificationBell from './NotificationBell';
import { logout } from '@/utils/auth';
import { switchToAccount } from '@/utils/switchAccount';

const getInitials = (name) => {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Where "View profile" lands. Students, teachers and staff have a self
// profile behind their digital ID card; admins have no Staff/Student
// record, so their nearest "profile" is the settings page they own.
const profileRouteFor = (role) => {
  if (role === 'superadmin') return '/superadmin/settings';
  if (role === 'schooladmin') return '/settings';
  return '/my-id-card';
};

const Header = ({ className, isMobileSidebarOpen = false, onMobileMenuClick, ...props }) => {
  const router = useRouter();
  const [selectedYear, setSelectedYear] = useState('2025-2026')
  const [userRole, setUserRole] = useState(null)
  const { user, school, isLoading: isLoadingProfile } = useUserProfile();
  const [associatedUsers, setAssociatedUsers] = useState([]);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileRef = useRef(null);

  const academicYearOptions = [
    { label: '2025-2026', value: '2025-2026' },
    { label: '2024-2025', value: '2024-2025' },
    { label: '2023-2024', value: '2023-2024' },
  ]

  const handleYearChange = (option) => {
    setSelectedYear(option.value)
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const role = sessionStorage.getItem('userRole')
      setUserRole(role)
    }

    const fetchAssociated = async () => {
      try {
        const token = document.cookie.split('; ').find(row => row.startsWith('kayease_token='))?.split('=')[1] || sessionStorage.getItem('authToken');
        if (!token) return;

        const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/users/associated`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (res.ok) {
          const data = await res.json();
          if (data.success && data.associatedUsers) {
            setAssociatedUsers(data.associatedUsers);
          }
        }
      } catch (e) {
        console.error('Failed to fetch associated users', e);
      }
    }

    fetchAssociated();

    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [])

  const handleSwitchAccount = switchToAccount;

  // Label each row with its school only when the list actually spans
  // more than one — a single-school list stays as uncluttered as before.
  const spansSchools =
    new Set(associatedUsers.map((u) => u.school?.schoolCode).filter(Boolean)).size > 1

  const shouldShowAcademicYear = userRole === 'schooladmin'

  return (
    <header
      className={twMerge(
        'w-full bg-header-background border-b border-header-border fixed top-0 left-0 right-0 z-40 xl:static',
        className
      )}
      {...props}
    >
      {/* Mobile Header Layout */}
      <div className="xl:hidden flex items-center justify-between px-4 py-3">
        {/* Left: School Logo */}
        <button
          onClick={() => {
            const dashboardRoute = userRole === 'superadmin' ? '/superadmin-dashboard'
              : userRole === 'teacher' ? '/teacher-dashboard'
                : userRole === 'student' ? '/student-dashboard'
                  : userRole === 'schooladmin' ? '/admin-dashboard'
                    : '/staff-dashboard';
            router.push(dashboardRoute);
          }}
          className="flex items-center hover:opacity-80 transition-opacity"
        >
          {userRole === 'superadmin' ? (
            <img
              src="/images/img_logo.png"
              alt="Kaympus "
              className="w-[90px] h-[25px] object-contain"
            />
          ) : (
            <SafeImage
              src={school?.logoUrl}
              alt={school?.schoolName || 'School Logo'}
              variant="logo"
              fallbackSrc="/images/img_logo.png"
              className="w-[90px] h-[25px] object-contain"
            />
          )}
        </button>

        {/* Right: Academic Year + Notification + Hamburger */}
        <div className="flex items-center gap-2">
          {/* Academic Year Dropdown - COMMENTED OUT */}
          {/* {shouldShowAcademicYear && (
            <div className="w-[120px]">
              <Dropdown
                placeholder={selectedYear}
                options={academicYearOptions}
                onChange={handleYearChange}
                className="w-full text-xs"
              />
            </div>
          )} */}

          {/* Notification Bell */}
          <NotificationBell />

          {/* Hamburger/Close Menu Button */}
          <button
            onClick={onMobileMenuClick}
            className="p-2 border border-border-primary rounded-md hover:bg-background-hover transition-colors duration-150"
            aria-label={isMobileSidebarOpen ? "Close menu" : "Open menu"}
          >
            {isMobileSidebarOpen ? (
              // Cross/X Icon
              <svg
                className="w-5 h-5 text-text-primary"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            ) : (
              // Hamburger Icon
              <Menu size={18} strokeWidth={2} />
            )}
          </button>
        </div>
      </div>

      {/* Desktop Header Layout */}
      <div className="hidden xl:flex items-center justify-end px-6 py-3">
        {/* Right Section */}
        <div className="flex items-center gap-4">
          {/* Academic Year Dropdown - COMMENTED OUT */}
          {/* {shouldShowAcademicYear && (
            <div className="w-64">
              <Dropdown
                placeholder={`Academic Year : ${selectedYear}`}
                options={academicYearOptions}
                onChange={handleYearChange}
                className="w-full"
              />
            </div>
          )} */}

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <NotificationBell />
            {userRole !== 'superadmin' && (
              <button
                type="button"
                className="p-2 border border-border-primary rounded-md hover:bg-gray-100 transition-colors duration-150"
                onClick={() => router.push('/messages')}
              >
                <img
                  src="/images/message.svg"
                  alt="Messages"
                  className="w-4 h-4"
                />
              </button>
            )}
          </div>

          {/* Profile Avatar & Dropdown */}
          <div className="relative ml-2" ref={profileRef}>
            <button
              className="flex items-center justify-center focus:outline-none transition-transform active:scale-95"
              onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              aria-label="Account menu"
              aria-haspopup="menu"
              aria-expanded={isProfileMenuOpen}
              data-testid="header-profile-trigger"
            >
              {user?.photoUrl ? (
                <SafeImage
                  src={user.photoUrl}
                  alt={user.name || 'Profile'}
                  variant="avatar"
                  name={user?.name}
                  className="w-9 h-9 rounded-full object-cover border-2 border-white shadow-sm ring-1 ring-gray-100"
                />
              ) : (
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ring-2 ring-white ${userRole === 'student' ? 'bg-yellow-100 text-yellow-700' :
                  userRole === 'teacher' ? 'bg-blue-100 text-blue-700' :
                    'bg-primary-background text-white'
                  }`}>
                  {getInitials(user?.name === 'User' && userRole === 'schooladmin' ? 'Administrator' : user?.name)}
                </div>
              )}
            </button>

            {/* Profile Dropdown */}
            {isProfileMenuOpen && (
              <div className="absolute right-0 top-full mt-3 w-72 bg-white rounded-2xl shadow-xl ring-1 ring-black/5 py-2 z-50 transform origin-top-right transition-all">
                {/* Current User Info */}
                <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50 rounded-t-2xl">
                  <div className="flex items-center gap-3">
                    {user?.photoUrl ? (
                      <SafeImage src={user.photoUrl} alt="" variant="avatar" name={user?.name} className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-md" />
                    ) : (
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold shadow-md border-2 border-white ${userRole === 'student' ? 'bg-yellow-100 text-yellow-700' :
                        userRole === 'teacher' ? 'bg-blue-100 text-blue-700' :
                          'bg-primary-background text-white'
                        }`}>
                        {getInitials(user?.name === 'User' && userRole === 'schooladmin' ? 'Administrator' : user?.name)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">
                        {user?.name === 'User' && userRole === 'schooladmin' ? 'Administrator' : (user?.name || 'User')}
                      </p>
                      <p className="text-xs text-gray-500 capitalize font-medium">{userRole}</p>
                    </div>
                    {/* Icon only — the row already says who this is. */}
                    <button
                      type="button"
                      onClick={() => {
                        setIsProfileMenuOpen(false);
                        router.push(profileRouteFor(userRole));
                      }}
                      title="View profile"
                      aria-label="View profile"
                      data-testid="header-view-profile"
                      className="w-9 h-9 shrink-0 grid place-items-center rounded-full bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100 transition-all hover:bg-brand-600 hover:text-white hover:ring-brand-600 hover:shadow-md active:scale-95"
                    >
                      <ChevronRight size={18} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>

                {/* Account Switcher List */}
                {associatedUsers.length > 0 && (
                  <div className="py-2">
                    <p className="px-5 py-2 text-[11px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                      Switch Account
                    </p>
                    <div className="max-h-64 overflow-y-auto px-2 space-y-1 custom-scrollbar">
                      {associatedUsers.map(u => {
                        const isActive = u.id === user?.id || u.id === user?._id;
                        return (
                          <button
                            key={u.id}
                            onClick={() => !isActive && handleSwitchAccount(u)}
                            disabled={isActive}
                            data-testid={`switch-account-${u.school?.schoolCode || 'school'}-${u.role}`}
                            className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all duration-200 group ${isActive
                              ? 'bg-brand-50 text-brand-700 cursor-default'
                              : 'hover:bg-gray-50 text-gray-700'
                              }`}
                          >
                            {u.photoUrl ? (
                              <SafeImage
                                src={u.photoUrl}
                                alt={u.name}
                                variant="avatar"
                                name={u.name}
                                className="w-9 h-9 rounded-lg object-cover border border-gray-200 shadow-sm flex-shrink-0 transition-transform group-hover:scale-105"
                              />
                            ) : (
                              <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 transition-transform group-hover:scale-105 ${u.role === 'student' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                                }`}>
                                <span className="font-bold text-xs">{getInitials(u.name)}</span>
                              </div>
                            )}
                            <div className="flex-1 text-left min-w-0">
                              <p className={`text-sm font-semibold truncate ${isActive ? 'text-brand-700' : 'text-gray-800'}`}>
                                {u.name === 'User' && u.role === 'schooladmin' ? 'Administrator' : u.name}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5 min-w-0">
                                <span className={`text-xs capitalize shrink-0 ${isActive ? 'text-brand-600/80' : 'text-gray-500'}`}>{u.role}</span>
                                {spansSchools && u.school?.schoolName && (
                                  <span
                                    className={`text-xs truncate ${isActive ? 'text-brand-600/80' : 'text-gray-400'}`}
                                    title={u.school.schoolName}
                                  >
                                    · {u.school.schoolName}
                                  </span>
                                )}
                                {isActive && (
                                  <span className="flex h-2 w-2 relative">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                  </span>
                                )}
                              </div>
                            </div>
                            {isActive && (
                              <div className="text-brand-600">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Logout Option */}
                <div className="border-t border-gray-100 mt-2 pt-2 px-2 pb-2">
                  <button
                    onClick={() => {
                      // Shared teardown, not a local copy: this used to clear
                      // storage itself and hard-code '/auth/sign-in', which
                      // dumped signed-out superadmins on the SCHOOL login form.
                      // `logout()` picks the form the session actually came from.
                      logout(router)
                    }}
                    className="w-full flex items-center gap-3 p-2.5 rounded-xl text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-100/50">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold">Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
